import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography, Paper, Divider, Button, TextField, IconButton} from 'views/components';
import {DeleteForever, Add, Mail, Phone} from 'views/icons';
import {Translate, translate} from 'views/module';

const ReferencesEdit = (props) => {
  const DEFAULT_REFERENCE_ENTRY = {
    name: '',
    relationship: '',
    phoneNumber: '',
    email: '',
  };

  const {bioQuestions, setFields, setIsComplete} = props;

  const [references, setReferences] = React.useState([DEFAULT_REFERENCE_ENTRY]);

  React.useEffect(() => {
    const sourceReferences = bioQuestions?.references || [];

    setReferences(sourceReferences.length ? sourceReferences : [DEFAULT_REFERENCE_ENTRY]);
  }, [bioQuestions]);

  React.useEffect(() => {
    const filteredReferences = references.filter((entry) => (
      typeof entry?.name === 'string' && entry.name.trim()
    ));
    setFields((prev) => ({...prev, references: filteredReferences}));
  }, [references, setFields]);

  React.useEffect(() => {
    setIsComplete(references.length > 0);
  }, [references, setIsComplete]);

  const handleChange = (index, field) => (value) => {
    setReferences((prev) => {
      return prev.map((entry, i) => {
        if (i !== index) return entry;

        return {
          ...entry,
          [field]: value,
        };
      });
    });
  };

  const handleAdd = () => {
    setReferences((prev) => [...prev, {...DEFAULT_REFERENCE_ENTRY}]);
  };

  const handleDelete = (index) => {
    setReferences((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6" textAlign="center">
        <Translate expression="caregiver.CaregiverBiography.References.Title" />
      </Typography>

      <Stack spacing={2.5}>
        {
          references.map((entry, index) => (
            <Paper key={`reference-${index}`} sx={{p: 2.5, borderRadius: 2}}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                    {entry.name?.trim() ||
                      translate('caregiver.CaregiverBiography.References.ReferenceLabel', {number: index + 1})}
                  </Typography>
                  <IconButton color="error" onClick={() => handleDelete(index)}>
                    <DeleteForever fontSize="small" />
                  </IconButton>
                </Stack>

                <Divider />

                <TextField
                  label={translate('caregiver.CaregiverBiography.References.Name.Label')}
                  placeholder={translate('caregiver.CaregiverBiography.References.Name.Placeholder')}
                  value={entry.name}
                  onChange={handleChange(index, 'name')}
                  fullWidth
                />

                <TextField
                  label={translate('caregiver.CaregiverBiography.References.Relationship.Label')}
                  placeholder={translate('caregiver.CaregiverBiography.References.Relationship.Placeholder')}
                  value={entry.relationship}
                  onChange={handleChange(index, 'relationship')}
                  fullWidth
                />

                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                  <TextField
                    label={translate('caregiver.CaregiverBiography.References.Phone.Label')}
                    placeholder={translate('caregiver.CaregiverBiography.References.Phone.Placeholder')}
                    value={entry.phoneNumber}
                    onChange={handleChange(index, 'phoneNumber')}
                    fullWidth
                  />
                  <TextField
                    label={translate('caregiver.CaregiverBiography.References.Email.Label')}
                    placeholder={translate('caregiver.CaregiverBiography.References.Email.Placeholder')}
                    value={entry.email}
                    onChange={handleChange(index, 'email')}
                    fullWidth
                  />
                </Stack>
              </Stack>
            </Paper>
          ))
        }
      </Stack>

      <Button
        variant="outlined"
        color="primary"
        onClick={handleAdd}
        startIcon={<Add />}
        sx={{alignSelf: 'center', borderStyle: 'dashed', borderWidth: 1, px: 3, py: 1.5}}
      >
        <Translate expression="caregiver.CaregiverBiography.References.AddReferenceButton" />
      </Button>

      {
        references.length > 0 && (
          <Paper sx={{p: 2.5, borderRadius: 2}}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                <Translate
                  expression="caregiver.CaregiverBiography.References.Summary.Title"
                  substitute={{
                    count: references.length,
                    references:
                      references.length === 1 ?
                        translate('caregiver.CaregiverBiography.References.Summary.ReferenceSingular') :
                        translate('caregiver.CaregiverBiography.References.Summary.ReferencePlural'),
                  }}
                />
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.75}>
                {
                  references.map((entry, index) => (
                    <Typography key={`summary-${index}`} variant="div">
                      <Typography component="span" variant="body2" sx={{fontWeight: 600}}>
                        {entry.name ||
                          translate('caregiver.CaregiverBiography.References.Summary.UnnamedReference')}
                      </Typography>
                      {entry.relationship && ` • ${entry.relationship}`}
                      {
                        entry.phoneNumber && (
                          <Stack direction='row' gap={1}>
                            <Phone fontSize='small' />
                            <Typography>{entry.phoneNumber}</Typography>
                          </Stack>
                        )
                      }
                      {
                        entry.email && (
                          <Stack direction='row' gap={1}>
                            <Mail fontSize='small' />
                            <Typography>{entry.email}</Typography>
                          </Stack>
                        )
                      }
                    </Typography>
                  ))
                }
              </Box>
            </Stack>
          </Paper>
        )
      }
    </Stack>
  );
};

ReferencesEdit.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  setFields: PropTypes.func.isRequired,
  setIsComplete: PropTypes.func,
};

export default React.memo(ReferencesEdit);
